import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { VersionUpdateBanner } from './VersionUpdateBanner.js';
import { CourseLevelSelector } from './CourseLevelSelector.js';
import { ProgrammeLevelSelector } from './ProgrammeLevelSelector.js';
import { fetchCourses, fetchQueueItems, type CourseSummary, type SessionIdentity } from '../api/client.js';
import {
  isItemScopedPath,
  nextCourseSubstitution,
  readCourseFromLocation,
  resolveCourseSelection,
  type CourseSubstitution,
} from '../lib/courseSelection.js';
import { buildCourseBrowserPath } from '../lib/authorNavigation.js';

export function AppShell({
  session,
  onLogout,
}: {
  session: SessionIdentity;
  onLogout: () => void;
}) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  /**
   * How the current location was reached, which is the only thing that separates "the reviewer asked
   * for a different course" from "the screen tidied its own address bar". See the substitution latch
   * below — without this distinction the two are indistinguishable and one of them has to be got
   * wrong.
   */
  const navigationType = useNavigationType();
  /**
   * ONE reading of "which course is this screen about?", under every spelling the app uses.
   *
   * `readCourseFromLocation` accepts the canonical `?courseKey`, the course browser's legacy
   * `?courseId`, and the `:courseId` segment of a `/courses/<id>/…` path. Asking only for
   * `courseKey` meant that standing in the browser on course `10b`, every tab here silently
   * addressed `12b` — see `lib/courseParam.ts` for the full account — and that a transcript link
   * arriving without a query string made this header announce the wrong exam level entirely.
   */
  const requestedCourseKey = readCourseFromLocation(location.pathname, searchParams);
  /**
   * The course actually IN FORCE, which is not always the one the URL asked for.
   * `resolveCourseSelection` prefers the URL, falls back to the level the reviewer last chose in
   * this tab, and discards either if this session holds no grant for it. Everything below — the tab
   * links, the badge count and the header identity — is built from this value and never from
   * `requestedCourseKey`, so what the header names is what the tabs open.
   */
  const { courseKey, source: courseSource } = resolveCourseSelection(session.courses, requestedCourseKey);
  /**
   * THE SUBSTITUTION SURVIVES THE SCREEN THAT PERFORMED IT.
   *
   * The rule, the retention contract and the evidence are all in
   * {@link nextCourseSubstitution} (`lib/courseSelection.ts`) — it is a pure function so that every
   * navigation shape can be tested without a browser. What lives here is only the wiring, and the
   * wiring is React's sanctioned **"adjust state while rendering"** pattern rather than a `useRef`.
   *
   * That choice is the answer to a specific hazard `gpt-5.6-sol` raised on 2026-08-28: a ref mutated
   * during a speculative render SURVIVES an aborted render, so a navigation that never committed
   * could leave a notice behind (or clear a committed one). State set during render is discarded
   * with the render attempt that set it, which is exactly the semantics wanted. It still costs no
   * extra frame, because the render below uses the value just computed rather than waiting for the
   * re-render that `setSubstitution` schedules.
   *
   * An effect would have been the obvious alternative and is wrong for the same reason it was wrong
   * for the structural badge above: an effect is one frame late.
   */
  const [substitution, setSubstitution] = useState<CourseSubstitution | null>(null);
  const currentSubstitution = nextCourseSubstitution(
    substitution,
    { key: location.key, navigationType },
    requestedCourseKey,
    courseKey,
  );
  if (currentSubstitution !== substitution) {
    setSubstitution(currentSubstitution);
  }
  const unavailableCourseKey = currentSubstitution?.requested ?? null;
  /**
   * Did anything actually NAME this level, or did the application pick one?
   *
   * On a course-wide list the fallback is a true statement about what is on screen — the queue
   * really will load that course's items. On a screen showing ONE lesson it is not: the item's own
   * course is whatever it is, and the header would be announcing a level nobody declared over
   * content that may belong to another. That is precisely the wire-crossing the reviewer described,
   * so an item screen accepts only a course the LOCATION named.
   */
  const levelIsDeclared = isItemScopedPath(location.pathname)
    ? courseSource === 'url'
    : courseKey !== '';
  /**
   * The pending structural count, CARRYING THE COURSE IT WAS COUNTED FOR.
   *
   * Storing a bare number made the badge a piece of state written by a completed load rather than a
   * reading of the course currently in force — so on a level switch the header flipped at once and
   * the badge went on showing the previous level's number for the length of the new level's fetch.
   * Reproduced in a real browser on 2026-08-28 with the response held back: **about two and a half
   * seconds of Level I's count sitting under a Level III heading.** A count belonging to one exam
   * level displayed under another level's name is the third load-bearing behaviour of this build
   * failing, and the same class of defect as the `estimated_minutes` casing trap (`L-1071`).
   *
   * Clearing it at the top of the effect would close most of the window and not all of it: an effect
   * runs after the commit, so the first render at the new course still paints the old number. The
   * pair is checked at RENDER time instead, which cannot be one frame late.
   */
  const [structuralPending, setStructuralPending] = useState<{ courseKey: string; count: number } | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [catalog, setCatalog] = useState<CourseSummary[]>([]);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const courseParam = courseKey ? `?courseKey=${encodeURIComponent(courseKey)}` : '';
  const structuralPath = `/queue/structural${courseParam}`;
  const wavesPath = `/waves${courseParam}`;
  const queuePath = `/queue${courseParam}`;
  const boardPath = `/board${courseParam}`;
  // The Courses tab used to drop the course entirely, so leaving the queue for the browser landed on
  // whatever the browser last remembered. It carries the course now, in the spelling the browser reads.
  const coursesPath = courseKey ? buildCourseBrowserPath(courseKey) : '/courses';

  /**
   * The course catalog, for the title and exam level the header names.
   *
   * `session.courses` is a list of ids and nothing else, so the level cannot come from there — and it
   * must never be derived from the id, which encodes nothing (`20b` is a Level I vintage, not Level
   * II; see `common/cfa-courses.ts`). A failure leaves the catalog empty and the header falls back to
   * naming the bare course key: less information, never wrong information, and never a blank shell.
   */
  useEffect(() => {
    let cancelled = false;
    void fetchCourses()
      .then((courses) => { if (!cancelled) setCatalog(courses); })
      .catch(() => { if (!cancelled) setCatalog([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!courseKey) {
      setStructuralPending(null);
      return;
    }
    let cancelled = false;
    void fetchQueueItems({ courseKey, status: 'pending' })
      .then((result) => {
        if (!cancelled) {
          // Course-scoped here as well as at the server. This badge is a COUNT, and a count that
          // silently totalled two exam levels is the same class of defect as the `estimated_minutes`
          // casing trap (`L-1071`) — a number that reads as one thing and means another.
          const structural = result.items.filter(
            (i) => i.taskType === 'structural' && i.courseKey === courseKey,
          );
          setStructuralPending({ courseKey, count: structural.length });
        }
      })
      .catch(() => { if (!cancelled) setStructuralPending(null); });
    return () => { cancelled = true; };
  }, [courseKey]);

  /**
   * The badge answers for the course in force, or it does not answer at all.
   *
   * This comparison is the whole fix for the stale count: a number counted for another course is
   * not a smaller or later truth about this one, it is a different question's answer, and the
   * honest rendering of "we have not counted this level yet" is nothing.
   */
  const structuralPendingCount =
    structuralPending && structuralPending.courseKey === courseKey ? structuralPending.count : null;

  useEffect(() => {
    if (!isUserMenuOpen) {
      return;
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (event.target instanceof Node && !userMenuRef.current?.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isUserMenuOpen]);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background-light text-slate-900">
      <VersionUpdateBanner />
      <header className="sticky top-0 z-50 flex items-center justify-between border-b border-slate-200 bg-white px-8 py-3">
        <div className="flex min-w-0 items-center gap-4">
          <div className="flex items-center gap-2 text-primary">
            <span className="material-symbols-outlined text-2xl">architecture</span>
            <h2 className="whitespace-nowrap text-lg font-bold tracking-tight">Mindojo Authoring</h2>
          </div>
          {/*
            NOT ON THE AUDIT LOG. `AuditLogPage` fetches the latest events across EVERY course and
            renders each row's own course; there is no sense in which that page is about one exam
            level. A course badge over it would be a header naming Level III above Level I rows —
            the same wire-crossing this control exists to prevent, wearing the fix's own clothes.
            The page says what it is scoped to instead.
          */}
          {location.pathname.startsWith('/audit') ? (
            <div
              className="flex items-baseline gap-2 rounded-lg bg-slate-100 px-3 py-1.5"
              data-testid="shell-course-identity"
              title="The audit log covers every course you can open, so no single course is selected here."
            >
              <span className="text-sm font-bold text-slate-800">All granted courses</span>
            </div>
          ) : location.pathname.startsWith('/programme') ? (
            <ProgrammeLevelSelector />
          ) : (
            <CourseLevelSelector
              catalog={catalog}
              courseKey={courseKey}
              declared={levelIsDeclared}
              grantedCourseIds={session.courses}
              requestedCourseKey={unavailableCourseKey}
            />
          )}
          <nav className="ml-4 flex items-center gap-5 whitespace-nowrap">
            <ShellTab label="Courses" to={coursesPath} />
            <ShellTab end label="Work Queue" to={queuePath} />
            <ShellTab label="Board" to={boardPath} />
            <ShellTab to={structuralPath}>
              Structural
              {structuralPendingCount != null && structuralPendingCount > 0 ? (
                <span
                  className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-100 px-1.5 text-[10px] font-bold text-amber-700"
                  data-testid="structural-pending-badge"
                >
                  {structuralPendingCount}
                </span>
              ) : null}
            </ShellTab>
            <ShellTab label="Waves" to={wavesPath} />
            <ShellTab label="Programme" to="/programme" />
            <ShellTab label="Audit Log" to="/audit" />
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative" ref={userMenuRef}>
            <button
              aria-expanded={isUserMenuOpen}
              aria-haspopup="menu"
              aria-label={`Open user menu for ${session.email}`}
              className="flex cursor-pointer items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 hover:bg-slate-200"
              onClick={() => setIsUserMenuOpen((open) => !open)}
              type="button"
            >
              <span className="text-sm font-semibold text-primary">{session.email}</span>
              <span aria-hidden="true" className="material-symbols-outlined text-slate-500">expand_more</span>
            </button>
            {isUserMenuOpen ? (
              <div
                className="absolute right-0 top-full z-50 mt-2 w-44 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                role="menu"
              >
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    onLogout();
                  }}
                  role="menuitem"
                  type="button"
                >
                  <span aria-hidden="true" className="material-symbols-outlined text-base text-slate-500">logout</span>
                  Log out
                </button>
              </div>
            ) : null}
          </div>
          <div className="flex size-9 items-center justify-center rounded-full bg-primary text-xs font-bold text-white ring-2 ring-slate-100">
            {(session.displayName || session.email).slice(0, 2).toUpperCase()}
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <Outlet />
      </div>
    </div>
  );
}

function ShellTab({ to, label, children, end }: { to: string; label?: string; children?: React.ReactNode; end?: boolean }) {
  return (
    <NavLink
      className={({ isActive }) =>
        isActive
          ? 'flex items-center border-b-2 border-primary -mb-[13px] pb-[11px] text-sm font-bold text-primary'
          : 'flex items-center text-sm font-medium text-slate-500 hover:text-primary'
      }
      end={end ?? false}
      to={to}
    >
      {children ?? label}
    </NavLink>
  );
}
